import { useId, useRef, useState } from 'react';
import { useConfirm } from '@/components/DialogProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteApartmentPhoto,
  issuePhotoUploadUrl,
  registerApartmentPhoto,
  reorderApartmentPhotos,
  updateApartmentPhoto,
} from '@/api/generated/sdk.gen';
import { ApiError } from '@/lib/api';
import { apartmentPhotosQuery, photoPublicURL, type ApartmentPhoto } from '@/lib/photos';
import { normalizeImage } from '@/lib/normalizeImage';
import { useToast } from '@/components/ToastProvider';

/**
 * Extract a human-readable error string from an unknown thrown value.
 *
 * Priority:
 *   1. Our ApiError: use the message field (already formatted by api.ts from
 *      problem.detail / problem.title / statusText).
 *   2. Anything with a non-empty `.detail` or `.title` string (e.g. ProblemJSON
 *      surfaces on the SDK's own error type).
 *   3. Anything with a non-empty `.message` string (covers Error subclasses and
 *      the SDK's CancelablePromise-based errors).
 *   4. Safe string fallback — never "[object Object]".
 */
function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e != null && typeof e === 'object') {
    const obj = e as Record<string, unknown>;
    if (typeof obj['detail'] === 'string' && obj['detail']) return obj['detail'];
    if (typeof obj['title'] === 'string' && obj['title']) return obj['title'];
    if (typeof obj['message'] === 'string' && obj['message']) return obj['message'];
  }
  if (typeof e === 'string' && e) return e;
  return 'An unexpected error occurred. Please try again.';
}

interface Props {
  apartmentID: string;
  canWrite: boolean;
}

export function ApartmentPhotos({ apartmentID, canWrite }: Props) {
  const { data: photos = [], error, isLoading } = useQuery(apartmentPhotosQuery(apartmentID));
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  const isUnavailable = error instanceof ApiError && error.status === 503;
  const errMsg = error instanceof ApiError ? error.message : error ? String(error) : null;

  return (
    <section className="mt-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Photos {photos.length > 0 && <span className="ml-1 text-foreground">({photos.length})</span>}
        </h2>
        {canWrite && (
          <PhotoUploadButton
            apartmentID={apartmentID}
            onError={setUploadErr}
          />
        )}
      </div>

      {uploadErr && (
        <p role="alert" className="mb-3 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive">
          {uploadErr}
        </p>
      )}

      {isLoading && <p className="text-sm text-muted">Loading…</p>}
      {errMsg && !isUnavailable && (
        <p role="alert" className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive">
          {errMsg}
        </p>
      )}

      {photos.length === 0 && !isLoading && !errMsg && (
        <p className="text-sm text-muted">No photos yet. Upload to start the gallery.</p>
      )}

      {photos.length > 0 && (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          {photos.map((p, i) => (
            <PhotoCard
              key={p.id}
              photo={p}
              apartmentID={apartmentID}
              canWrite={canWrite}
              canMoveUp={i > 0}
              canMoveDown={i < photos.length - 1}
              prevPhoto={photos[i - 1]}
              nextPhoto={photos[i + 1]}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Upload ──────────────────────────────────────────────────────────────────

function PhotoUploadButton({
  apartmentID,
  onError,
}: {
  apartmentID: string;
  onError: (msg: string | null) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const inputId = useId();

  const uploadOne = async (file: File): Promise<void> => {
    // 0. Cap the longest side before anything else (OH-115). This has to come
    //    first: the presigned URL signs Content-Length, so the size we declare
    //    in step 1 must be the size we actually PUT in step 2. Phone originals
    //    ran to 6341px / ~10 MB while the site never renders above ~1920.
    const normalized = await normalizeImage(file);

    // 1. POST upload-url — server picks the R2 key.
    const issued = await issuePhotoUploadUrl({
      throwOnError: true,
      path: { id: apartmentID },
      body: { mime: normalized.type, size_bytes: normalized.blob.size },
    });
    const url = issued.data.upload_url.toString();
    const headers: Record<string, string> = { ...issued.data.headers };

    // 2. PUT bytes directly to R2.
    const putRes = await fetch(url, { method: 'PUT', headers, body: normalized.blob });
    if (!putRes.ok) {
      throw new Error(`R2 upload failed: HTTP ${putRes.status}`);
    }

    // 3. Register the row. Dimensions ride along: the API has always accepted
    //    them, but this uploader never sent any, so every photo added through
    //    the admin landed with width/height NULL — invisible until something
    //    needed to reason about the file's real size.
    await registerApartmentPhoto({
      throwOnError: true,
      path: { id: apartmentID },
      body: {
        photo_id: issued.data.photo_id,
        r2_key: issued.data.key,
        mime: normalized.type,
        ...(normalized.width > 0 && normalized.height > 0
          ? { width: normalized.width, height: normalized.height }
          : {}),
      },
    });
  };

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    onError(null);
    setUploading(true);
    const count = files.length;
    try {
      for (const f of Array.from(files)) {
        try {
          await uploadOne(f);
        } catch (err) {
          if (err instanceof ApiError && err.status === 503) {
            onError('Photo upload is unavailable: the server is missing R2 credentials.');
            return;
          }
          onError(errorMessage(err));
          return;
        }
      }
      // Reached only when every file uploaded successfully.
      qc.invalidateQueries({ queryKey: ['apartments', apartmentID, 'photos'] });
      toast({
        kind: 'success',
        title: count === 1 ? 'Photo uploaded' : `${count} photos uploaded`,
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <label
        htmlFor={inputId}
        aria-busy={uploading || undefined}
        className="inline-flex h-8 cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border border-border bg-card px-4 text-sm font-semibold text-primary transition hover:bg-[var(--ollio-navy-light)] hover:border-primary"
      >
        {uploading ? 'Uploading…' : 'Upload photos'}
      </label>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        disabled={uploading}
        onChange={(e) => void onFiles(e.target.files)}
        className="hidden"
      />
    </div>
  );
}

// ─── Per-photo card ──────────────────────────────────────────────────────────

function PhotoCard({
  photo,
  apartmentID,
  canWrite,
  canMoveUp,
  canMoveDown,
  prevPhoto,
  nextPhoto,
}: {
  photo: ApartmentPhoto;
  apartmentID: string;
  canWrite: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  prevPhoto: ApartmentPhoto | undefined;
  nextPhoto: ApartmentPhoto | undefined;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [err, setErr] = useState<string | null>(null);
  const askConfirm = useConfirm();

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['apartments', apartmentID, 'photos'] });

  const setCover = useMutation({
    mutationFn: () =>
      updateApartmentPhoto({
        throwOnError: true,
        path: { id: apartmentID, photo_id: photo.id },
        body: { is_cover: true },
      }),
    onSuccess: () => {
      invalidate();
      toast({ kind: 'success', title: 'Cover photo updated' });
    },
    onError: (e) => setErr(errorMessage(e)),
  });

  const del = useMutation({
    mutationFn: () =>
      deleteApartmentPhoto({
        throwOnError: true,
        path: { id: apartmentID, photo_id: photo.id },
      }),
    onSuccess: () => {
      invalidate();
      toast({ kind: 'success', title: 'Photo deleted' });
    },
    onError: (e) => setErr(errorMessage(e)),
  });

  // swap (this.sort_order, neighbour.sort_order) — both in one batch tx
  // so the partial reorder is never visible.
  const swap = useMutation({
    mutationFn: (other: ApartmentPhoto) =>
      reorderApartmentPhotos({
        throwOnError: true,
        path: { id: apartmentID },
        body: {
          orders: [
            { photo_id: photo.id, sort_order: other.sort_order },
            { photo_id: other.id, sort_order: photo.sort_order },
          ],
        },
      }),
    onSuccess: invalidate,
    onError: (e) => setErr(errorMessage(e)),
  });

  const url = photoPublicURL(photo);
  const busy = setCover.isPending || del.isPending || swap.isPending;

  return (
    <li className="relative overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
      <div className="relative aspect-[4/3] w-full bg-[var(--surface-sunken)]">
        {url ? (
          <img
            src={url}
            alt={photo.alt ?? ''}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-muted">
            (R2 public URL not configured)
          </div>
        )}
        {photo.is_cover && (
          <span className="absolute left-2 top-2 inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
            Cover
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-1 px-3 py-2 text-xs">
        <span className="text-muted">#{photo.sort_order}</span>
        {canWrite && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              title="Move up"
              disabled={busy || !canMoveUp}
              onClick={() => prevPhoto && swap.mutate(prevPhoto)}
              className="rounded-[var(--radius-control)] border border-border bg-card px-2 py-0.5 text-primary transition hover:bg-[var(--surface-sunken)] disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              title="Move down"
              disabled={busy || !canMoveDown}
              onClick={() => nextPhoto && swap.mutate(nextPhoto)}
              className="rounded-[var(--radius-control)] border border-border bg-card px-2 py-0.5 text-primary transition hover:bg-[var(--surface-sunken)] disabled:opacity-30"
            >
              ↓
            </button>
            {!photo.is_cover && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setCover.mutate()}
                className="rounded-[var(--radius-control)] border border-border bg-card px-2 py-0.5 text-primary transition hover:bg-[var(--surface-sunken)] disabled:opacity-30"
              >
                Set cover
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                const ok = await askConfirm({
                  title: 'Delete this photo?',
                  destructive: true,
                  confirmLabel: 'Delete',
                });
                if (ok) del.mutate();
              }}
              className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-card px-2 py-0.5 text-destructive transition hover:bg-[var(--ollio-danger-bg)] disabled:opacity-30"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {err && (
        <p role="alert" className="border-t border-[var(--border-hairline)] bg-[var(--ollio-danger-bg)] px-3 py-1 text-xs text-destructive">
          {err}
        </p>
      )}
    </li>
  );
}
